import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useMockPortal } from '../mock/useMockPortal';
import { formatPortalCurrency, formatPortalDate } from '../mock/store';
import {
  ELF_TEAL,
  elfOutlinedButtonSx,
  elfPrimaryButtonSx,
  elfTextButtonSx,
} from '../elfTheme';

const LoanHome: React.FC = () => {
  const { loan, recentActivity, account } = useMockPortal();
  const displayName = localStorage.getItem('first_name') || account.firstName;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Hello, {displayName}
          </Typography>
          <Typography color="text.secondary">
            {loan.programLabel} · Loan {loan.loanNumber}
          </Typography>
        </Box>
        <Chip
          label={loan.status}
          variant="outlined"
          sx={{
            borderColor: ELF_TEAL,
            color: ELF_TEAL,
            fontWeight: 600,
          }}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current balance
          </Typography>
          <Typography variant="h4" component="p" sx={{ color: ELF_TEAL }}>
            {formatPortalCurrency(loan.totalBalance)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Principal {formatPortalCurrency(loan.principalBalance)} · Interest{' '}
            {formatPortalCurrency(loan.interestBalance)}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Next payment
          </Typography>
          <Typography variant="h4" component="p">
            {formatPortalCurrency(loan.nextPaymentAmount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Due {formatPortalDate(loan.nextPaymentDate)}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Account status
          </Typography>
          <Typography variant="h5" component="p" sx={{ mt: 0.5 }}>
            {loan.status}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Payments are up to date in this preview.
          </Typography>
        </Paper>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 4 }}
      >
        <Button
          component={RouterLink}
          to="/portal/loans/pay"
          variant="contained"
          size="large"
          sx={elfPrimaryButtonSx}
        >
          Make a payment
        </Button>
        <Button
          component={RouterLink}
          to="/portal/loans/documents"
          variant="outlined"
          sx={elfOutlinedButtonSx}
        >
          Documents
        </Button>
        <Button
          component={RouterLink}
          to="/portal/loans/history"
          variant="outlined"
          sx={elfOutlinedButtonSx}
        >
          Payment history
        </Button>
        <Button
          component={RouterLink}
          to="/portal/account"
          variant="text"
          sx={elfTextButtonSx}
        >
          Update contact info
        </Button>
      </Stack>

      <Typography variant="h6" gutterBottom>
        Recent activity
      </Typography>
      <Paper variant="outlined">
        {recentActivity.map((item, index) => (
          <Box
            key={item.id}
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              borderBottom: index < recentActivity.length - 1 ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="body2">{item.description}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatPortalDate(item.date)} · {item.status}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {formatPortalCurrency(item.amount)}
            </Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default LoanHome;
